<template>
  <div v-if="rendered" class="em-root">
    <!-- HEADER -->
    <div class="em-header">
      <div class="em-header-left">
        <div class="em-header-icon">
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.2"
          >
            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
          </svg>
        </div>
        <div>
          <h1 class="em-title">Evolução de Clientes</h1>
          <p class="em-sub">Estatísticas mensais da base de clientes</p>
        </div>
      </div>
      <div class="em-controls">
        <div class="em-periodo-group">
          <button
            v-for="p in periodos"
            :key="p.value"
            class="em-periodo-btn"
            :class="{ active: periodoSelecionado === p.value }"
            @click="periodoSelecionado = p.value"
          >
            {{ p.label }}
          </button>
        </div>
      </div>
    </div>

    <!-- LOADING -->
    <div v-if="loading" class="em-loading">
      <span class="em-spinner"></span> A carregar estatísticas…
    </div>

    <!-- EMPTY -->
    <div v-else-if="!dadosFiltrados.length" class="em-empty">
      <div class="em-empty-icon">
        <svg
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
        >
          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
        </svg>
      </div>
      <h3>Sem dados disponíveis</h3>
      <p>As estatísticas são calculadas no início de cada mês.</p>
    </div>

    <!-- TABELA -->
    <div v-else class="em-table-wrap">
      <div class="em-scroll">
        <table class="em-table">
          <thead>
            <tr>
              <th class="em-th em-th-metric">Métrica</th>
              <th
                v-for="row in dadosFiltrados"
                :key="row.mes"
                class="em-th em-th-mes"
              >
                {{ fmtMes(row.mes) }}
              </th>
            </tr>
          </thead>
          <tbody>
            <!-- Total Cartões — não clicável -->
            <tr class="em-row em-row-group-header">
              <td class="em-td em-td-metric">
                <div class="em-metric-name">Total Clientes</div>
                <div class="em-metric-desc">Total de cartões válidos</div>
              </td>
              <td
                v-for="row in dadosFiltrados"
                :key="row.mes"
                class="em-td em-td-val"
              >
                {{ row.total_cartoes.toLocaleString("pt-PT") }}
              </td>
            </tr>

            <!-- Novos -->
            <tr class="em-row">
              <td class="em-td em-td-metric">
                <div class="em-metric-name em-color-green">Novos</div>
                <div class="em-metric-desc">Cartões emitidos no mês</div>
              </td>
              <td
                v-for="row in dadosFiltrados"
                :key="row.mes"
                class="em-td em-td-val em-td-clickable"
                @click="abrirDetalhe(row.mes, 'novos')"
              >
                <span class="em-badge em-badge-green"
                  >+{{ row.novos_cartoes.toLocaleString("pt-PT") }}</span
                >
              </td>
            </tr>

            <!-- Ativos 60d -->
            <tr class="em-row">
              <td class="em-td em-td-metric">
                <div class="em-metric-name em-color-orange">Ativos</div>
                <div class="em-metric-desc">
                  Cartões com consumos nos últimos 60D
                </div>
              </td>
              <td
                v-for="row in dadosFiltrados"
                :key="row.mes"
                class="em-td em-td-val em-td-clickable"
                @click="abrirDetalhe(row.mes, 'ativos')"
              >
                {{ row.ativos_60d.toLocaleString("pt-PT") }}
              </td>
            </tr>

            <!-- Inativos 60d -->
            <tr class="em-row">
              <td class="em-td em-td-metric">
                <div class="em-metric-name em-color-grey">Inativos 60D</div>
                <div class="em-metric-desc">
                  Cartões sem consumos nos últimos 60D
                </div>
              </td>
              <td
                v-for="row in dadosFiltrados"
                :key="row.mes"
                class="em-td em-td-val em-val-muted em-td-clickable"
                @click="abrirDetalhe(row.mes, 'inativos')"
              >
                {{ row.inativos_60d.toLocaleString("pt-PT") }}
              </td>
            </tr>

            <!-- Reativados -->
            <tr class="em-row">
              <td class="em-td em-td-metric">
                <div class="em-metric-name em-color-blue">Reativados</div>
                <div class="em-metric-desc">
                  Clientes com consumo no mês que estavam inativos no anterior
                </div>
              </td>
              <td
                v-for="row in dadosFiltrados"
                :key="row.mes"
                class="em-td em-td-val em-td-clickable"
                @click="abrirDetalhe(row.mes, 'reativados')"
              >
                <span class="em-badge em-badge-blue">{{
                  row.reativados.toLocaleString("pt-PT")
                }}</span>
              </td>
            </tr>

            <!-- Inativados -->
            <tr class="em-row">
              <td class="em-td em-td-metric">
                <div class="em-metric-name em-color-red">Perdidos</div>
                <div class="em-metric-desc">
                  Clientes ativos no mês anterior que ficaram inativos
                </div>
              </td>
              <td
                v-for="row in dadosFiltrados"
                :key="row.mes"
                class="em-td em-td-val em-td-clickable"
                @click="abrirDetalhe(row.mes, 'inativados')"
              >
                <span class="em-badge em-badge-red">{{
                  (row.inativados ?? 0).toLocaleString("pt-PT")
                }}</span>
              </td>
            </tr>

            <!-- Separador -->
            <tr class="em-row em-row-separator">
              <td :colspan="dadosFiltrados.length + 1" class="em-td-separator">
                Percentagens
              </td>
            </tr>

            <!-- % Crescimento -->
            <tr class="em-row">
              <td class="em-td em-td-metric">
                <div class="em-metric-name">% Crescimento</div>
                <div class="em-metric-desc">Novos / Total</div>
              </td>
              <td
                v-for="row in dadosFiltrados"
                :key="row.mes"
                class="em-td em-td-val"
              >
                <span class="em-perc" :class="percClass(row.perc_crescimento)"
                  >{{ row.perc_crescimento }}%</span
                >
              </td>
            </tr>

            <!-- % Ativos -->
            <tr class="em-row">
              <td class="em-td em-td-metric">
                <div class="em-metric-name">% Ativos</div>
                <div class="em-metric-desc">Ativos 60D / Total</div>
              </td>
              <td
                v-for="row in dadosFiltrados"
                :key="row.mes"
                class="em-td em-td-val"
              >
                <span class="em-perc" :class="percClass(row.perc_ativos)"
                  >{{ row.perc_ativos }}%</span
                >
              </td>
            </tr>

            <!-- % Reativação -->
            <tr class="em-row">
              <td class="em-td em-td-metric">
                <div class="em-metric-name">% Reativação</div>
                <div class="em-metric-desc">
                  Reativados / Inativos mês anterior
                </div>
              </td>
              <td
                v-for="row in dadosFiltrados"
                :key="row.mes"
                class="em-td em-td-val"
              >
                <span class="em-perc" :class="percClass(row.perc_reativacao)"
                  >{{ row.perc_reativacao }}%</span
                >
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- MODAL DETALHE CLIENTES -->
    <transition name="em-fade">
      <div
        v-if="modalAberto"
        class="em-overlay"
        @click.self="modalAberto = false"
      >
        <div class="em-modal">
          <div class="em-modal-head">
            <div>
              <div class="em-modal-title">
                {{ tipoLabel }} — {{ fmtMes(modalMes) }}
              </div>
              <div class="em-modal-sub" v-if="!loadingModal">
                {{ modalClientes.length }} clientes
              </div>
            </div>
            <button class="em-modal-close" @click="modalAberto = false">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
              >
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div
            class="em-modal-search"
            v-if="!loadingModal && modalClientes.length"
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <input
              v-model="searchModal"
              class="em-search-input"
              placeholder="Filtrar por nome ou email…"
            />
          </div>

          <div class="em-modal-body">
            <!-- Loading -->
            <div v-if="loadingModal" class="em-loading">
              <span class="em-spinner"></span> A carregar clientes…
            </div>

            <!-- Lista -->
            <div v-else-if="clientesFiltrados.length" class="em-clientes-list">
              <div
                v-for="c in clientesFiltrados"
                :key="c.qr_code"
                class="em-cliente-row"
              >
                <div class="em-cliente-avatar">
                  {{ (c.nome || "?")[0].toUpperCase() }}
                </div>
                <div class="em-cliente-info">
                  <span class="em-cliente-nome">{{ c.nome || "—" }}</span>
                  <span class="em-cliente-email">{{
                    c.email || c.qr_code
                  }}</span>
                </div>
                <div class="em-cliente-meta">
                  <span v-if="c.ultima_visita" class="em-cliente-tag">
                    Última visita: {{ fmtDate(c.ultima_visita) }}
                  </span>
                  <span
                    v-if="c.data_registo"
                    class="em-cliente-tag em-tag-green"
                  >
                    Registado: {{ fmtDate(c.data_registo) }}
                  </span>
                  <span
                    v-if="c.visitas_no_periodo"
                    class="em-cliente-tag em-tag-orange"
                  >
                    {{ c.visitas_no_periodo }} visita{{
                      c.visitas_no_periodo > 1 ? "s" : ""
                    }}
                  </span>
                </div>
              </div>
            </div>

            <div v-else class="em-empty-modal">Nenhum cliente encontrado.</div>
          </div>

          <div class="em-modal-foot">
            <button class="em-btn-ghost" @click="modalAberto = false">
              Fechar
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
  <LoadingOverlay v-else />
</template>

<script>
  import LoadingOverlay from "@/components/Loading/LoadingOverlay";
  import { estatisticasService } from "@/services";

  const TIPO_LABELS = {
    novos: "Novos Clientes",
    ativos: "Clientes Ativos 60D",
    inativos: "Clientes Inativos 60D",
    reativados: "Clientes Reativados",
    inativados: "Clientes Inativados",
  };

  export default {
    name: "EvolucaoMensal",
    components: { LoadingOverlay },
    data() {
      return {
        rendered: false,
        loading: false,
        dados: [],
        periodoSelecionado: 12,
        periodos: [
          { label: "3 meses", value: 3 },
          { label: "4 meses", value: 4 },
          { label: "6 meses", value: 6 },
          { label: "1 ano", value: 12 },
        ],
        // Modal
        modalAberto: false,
        loadingModal: false,
        modalMes: null,
        modalTipo: null,
        modalClientes: [],
        searchModal: "",
      };
    },
    computed: {
      dadosFiltrados() {
        return this.dados.slice(-this.periodoSelecionado);
      },
      tipoLabel() {
        return TIPO_LABELS[this.modalTipo] || "";
      },
      clientesFiltrados() {
        if (!this.searchModal) return this.modalClientes;
        const q = this.searchModal.toLowerCase();
        return this.modalClientes.filter(
          (c) =>
            (c.nome || "").toLowerCase().includes(q) ||
            (c.email || "").toLowerCase().includes(q),
        );
      },
    },
    methods: {
      fmtMes(mes) {
        const [ano, m] = mes.split("-");
        const nomes = [
          "Jan",
          "Fev",
          "Mar",
          "Abr",
          "Mai",
          "Jun",
          "Jul",
          "Ago",
          "Set",
          "Out",
          "Nov",
          "Dez",
        ];
        return `${nomes[parseInt(m) - 1]} ${ano}`;
      },
      fmtDate(d) {
        if (!d) return "—";
        return new Date(d).toLocaleDateString("pt-PT");
      },
      percClass(val) {
        if (val >= 30) return "perc-high";
        if (val >= 15) return "perc-mid";
        return "perc-low";
      },
      async abrirDetalhe(mes, tipo) {
        this.modalMes = mes;
        this.modalTipo = tipo;
        this.modalClientes = [];
        this.searchModal = "";
        this.loadingModal = true;
        this.modalAberto = true;

        try {
          const resp = await estatisticasService.getDetalhe(mes, tipo);
          this.modalClientes = resp.clientes || [];
        } catch (err) {
          console.error("Erro ao carregar detalhe:", err);
          this.$notify({
            type: "danger",
            title: "Erro",
            message: "Não foi possível carregar os clientes.",
          });
        } finally {
          this.loadingModal = false;
        }
      },
      async carregar() {
        this.loading = true;
        try {
          const resp = await estatisticasService.getMensais(24);
          this.dados = resp.dados || [];
        } catch (err) {
          console.error("Erro ao carregar estatísticas:", err);
          this.$notify({
            type: "danger",
            title: "Erro",
            message: "Não foi possível carregar as estatísticas.",
          });
        } finally {
          this.loading = false;
        }
      },
    },
    async mounted() {
      await this.carregar();
      this.rendered = true;
    },
  };
</script>

<style scoped>
  .em-root {
    padding: 28px 28px 48px;
    font-family: "DM Sans", "Outfit", system-ui, sans-serif;
    min-height: 100vh;
    background: #f8f6f3;
  }
  .em-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
    gap: 16px;
    flex-wrap: wrap;
  }
  .em-header-left {
    display: flex;
    align-items: center;
    gap: 14px;
  }
  .em-header-icon {
    width: 44px;
    height: 44px;
    flex-shrink: 0;
    background: #ff825d;
    border-radius: 13px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    box-shadow: 0 4px 14px rgba(255, 130, 93, 0.3);
  }
  .em-title {
    font-size: 20px;
    font-weight: 800;
    color: #1a1210;
    margin: 0;
    letter-spacing: -0.4px;
  }
  .em-sub {
    font-size: 13px;
    color: #b0a8a0;
    margin: 3px 0 0;
  }

  .em-periodo-group {
    display: flex;
    gap: 4px;
  }
  .em-periodo-btn {
    padding: 7px 14px;
    border: 1.5px solid #e8e4e0;
    border-radius: 20px;
    background: #fff;
    font-size: 13px;
    font-weight: 600;
    color: #7a736e;
    cursor: pointer;
    font-family: inherit;
    transition: all 0.15s;
    white-space: nowrap;
  }
  .em-periodo-btn:hover {
    border-color: #ff825d;
    color: #ff825d;
  }
  .em-periodo-btn.active {
    background: #ff825d;
    border-color: #ff825d;
    color: #fff;
  }

  .em-loading {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
    color: #b0a8a0;
    padding: 40px 0;
    justify-content: center;
  }
  .em-spinner {
    width: 16px;
    height: 16px;
    border: 2px solid #e8e4e0;
    border-top-color: #ff825d;
    border-radius: 50%;
    animation: em-spin 0.6s linear infinite;
    flex-shrink: 0;
  }
  @keyframes em-spin {
    to {
      transform: rotate(360deg);
    }
  }

  .em-empty {
    text-align: center;
    padding: 80px 20px;
    color: #b0a8a0;
  }
  .em-empty-icon {
    width: 72px;
    height: 72px;
    border-radius: 20px;
    background: #f5f2ef;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
    color: #c8c0b8;
  }
  .em-empty h3 {
    font-size: 18px;
    font-weight: 700;
    color: #5a534f;
    margin-bottom: 8px;
  }
  .em-empty p {
    font-size: 14px;
    color: #b0a8a0;
    margin: 0;
  }

  .em-table-wrap {
    background: #fff;
    border: 1.5px solid #f0ece8;
    border-radius: 16px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 4px 14px rgba(0, 0, 0, 0.05);
    overflow: hidden;
  }
  .em-scroll {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  .em-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13.5px;
  }
  .em-th {
    padding: 11px 16px;
    background: #faf8f6;
    border-bottom: 1px solid #f0ece8;
    font-size: 11px;
    font-weight: 700;
    color: #b0a8a0;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    text-align: left;
    white-space: nowrap;
  }
  .em-th-metric {
    min-width: 220px;
    position: sticky;
    left: 0;
    background: #faf8f6;
    z-index: 2;
  }
  .em-th-mes {
    min-width: 90px;
    text-align: center;
  }

  .em-row {
    border-bottom: 1px solid #f8f5f2;
  }
  .em-row:last-child {
    border-bottom: none;
  }
  .em-row:hover {
    background: #fdfcfb;
  }
  .em-row-group-header {
    background: #fdfcfb;
  }

  .em-td {
    padding: 12px 16px;
    vertical-align: middle;
  }
  .em-td-metric {
    position: sticky;
    left: 0;
    background: inherit;
    z-index: 1;
    border-right: 1px solid #f0ece8;
    min-width: 220px;
  }
  .em-row:hover .em-td-metric {
    background: #fdfcfb;
  }
  .em-row-group-header .em-td-metric {
    background: #fdfcfb;
  }
  .em-td-val {
    text-align: center;
    font-weight: 700;
    color: #1a1210;
  }
  .em-val-muted {
    color: #8a857e;
    font-weight: 500;
  }

  /* Clicável */
  .em-td-clickable {
    cursor: pointer;
    transition: background 0.1s;
  }
  .em-td-clickable:hover {
    background: #fff8f5 !important;
  }
  .em-td-clickable:hover .em-badge,
  .em-td-clickable:hover {
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  .em-metric-name {
    font-size: 13.5px;
    font-weight: 700;
    color: #1a1210;
  }
  .em-metric-desc {
    font-size: 11.5px;
    color: #b0a8a0;
    margin-top: 2px;
  }
  .em-color-green {
    color: #0a7a4a;
  }
  .em-color-orange {
    color: #ff825d;
  }
  .em-color-grey {
    color: #8a857e;
  }
  .em-color-blue {
    color: #3b82f6;
  }
  .em-color-red {
    color: #e53935;
  }

  .em-badge {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 700;
  }
  .em-badge-green {
    background: #e8faf2;
    color: #0a7a4a;
  }
  .em-badge-blue {
    background: #eff6ff;
    color: #3b82f6;
  }
  .em-badge-red {
    background: #fdecea;
    color: #e53935;
  }

  .em-perc {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 20px;
    font-size: 12.5px;
    font-weight: 700;
  }
  .perc-high {
    background: #e8faf2;
    color: #0a7a4a;
  }
  .perc-mid {
    background: #fff8eb;
    color: #d97706;
  }
  .perc-low {
    background: #f5f2ef;
    color: #8a857e;
  }

  .em-row-separator td {
    padding: 6px 16px;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #c8c0b8;
    background: #f8f6f3;
    border-top: 1px solid #f0ece8;
    border-bottom: 1px solid #f0ece8;
  }
  .em-td-separator {
    position: sticky;
    left: 0;
  }

  /* MODAL */
  .em-overlay {
    position: fixed;
    inset: 0;
    background: rgba(15, 10, 8, 0.45);
    backdrop-filter: blur(6px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1200;
    padding: 16px;
  }
  .em-modal {
    background: #fff;
    border-radius: 18px;
    width: 100%;
    max-width: 560px;
    max-height: 88vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 24px 80px rgba(0, 0, 0, 0.2);
    overflow: hidden;
  }
  .em-modal-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 20px 24px 16px;
    border-bottom: 1px solid #f5f2ef;
    gap: 14px;
  }
  .em-modal-title {
    font-size: 16px;
    font-weight: 800;
    color: #1a1210;
    letter-spacing: -0.3px;
  }
  .em-modal-sub {
    font-size: 13px;
    color: #b0a8a0;
    margin-top: 3px;
  }
  .em-modal-close {
    background: #f7f4f1;
    border: none;
    cursor: pointer;
    width: 30px;
    height: 30px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #7a736e;
    transition: all 0.12s;
    flex-shrink: 0;
  }
  .em-modal-close:hover {
    background: #f0ece8;
    color: #1a1210;
  }

  .em-modal-search {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 24px;
    border-bottom: 1px solid #f5f2ef;
    background: #fdfcfb;
  }
  .em-modal-search svg {
    color: #b0a8a0;
    flex-shrink: 0;
  }
  .em-search-input {
    background: none;
    border: none;
    outline: none;
    font-size: 13.5px;
    color: #1a1210;
    width: 100%;
    font-family: inherit;
  }
  .em-search-input::placeholder {
    color: #c0b8b0;
  }

  .em-modal-body {
    padding: 16px 24px;
    overflow-y: auto;
    flex: 1;
  }
  .em-modal-foot {
    padding: 14px 24px;
    border-top: 1px solid #f5f2ef;
    display: flex;
    justify-content: flex-end;
  }
  .em-btn-ghost {
    padding: 9px 18px;
    background: #f7f4f1;
    border: none;
    border-radius: 10px;
    font-size: 13.5px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
    color: #5a534f;
    transition: background 0.15s;
  }
  .em-btn-ghost:hover {
    background: #f0ece8;
  }

  .em-clientes-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .em-cliente-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    border-radius: 10px;
    background: #faf8f6;
    border: 1.5px solid #f0ece8;
  }
  .em-cliente-avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #f0ece8;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 700;
    color: #7a736e;
    flex-shrink: 0;
  }
  .em-cliente-info {
    flex: 1;
    min-width: 0;
  }
  .em-cliente-nome {
    display: block;
    font-size: 13.5px;
    font-weight: 600;
    color: #1a1210;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .em-cliente-email {
    display: block;
    font-size: 12px;
    color: #b0a8a0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .em-cliente-meta {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 3px;
    flex-shrink: 0;
  }
  .em-cliente-tag {
    font-size: 11px;
    font-weight: 600;
    color: #8a857e;
    background: #f0ece8;
    padding: 2px 7px;
    border-radius: 20px;
    white-space: nowrap;
  }
  .em-tag-green {
    background: #e8faf2;
    color: #0a7a4a;
  }
  .em-tag-orange {
    background: #fff0eb;
    color: #c04820;
  }

  .em-empty-modal {
    text-align: center;
    padding: 32px 0;
    font-size: 13px;
    color: #b0a8a0;
  }

  .em-fade-enter-active {
    transition: opacity 0.2s;
  }
  .em-fade-leave-active {
    transition: opacity 0.15s;
  }
  .em-fade-enter,
  .em-fade-leave-to {
    opacity: 0;
  }

  @media (max-width: 600px) {
    .em-root {
      padding: 16px 12px 40px;
    }
    .em-periodo-group {
      flex-wrap: wrap;
    }
    .em-cliente-meta {
      display: none;
    }
  }
</style>
